import { apiRequest } from "@/lib/api/http";
import {
  DEFAULT_EVENT_CATEGORIES,
  type NotificationCategory,
  type VehicleEventType,
} from "@/lib/notifications/vehicle-events";

/**
 * Per-user toast and sound preferences, stored upstream (`docs/Notifications.MD`). Every type's
 * toast defaults to on and its sound to off, including types added after the user last saved — so
 * a type missing from `toast` means `true` and one missing from `sound` means `false`.
 */
export interface NotificationPrefs {
  toast: Record<VehicleEventType, boolean>;
  sound: Record<VehicleEventType, boolean>;
  eventTypes: VehicleEventType[];
  categories: Record<string, VehicleEventType[]>;
}

interface RemotePrefsData {
  toast?: Record<string, unknown>;
  sound?: Record<string, unknown>;
}

interface RemotePrefsResponse {
  success: true;
  data?: RemotePrefsData;
  event_types?: unknown;
  categories?: unknown;
}

/** Upstream promises real booleans; anything else is dropped so it falls back to the default. */
function mapBooleanMap(raw: Record<string, unknown> | undefined): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const [type, value] of Object.entries(raw ?? {})) {
    if (typeof value === "boolean") map[type] = value;
  }
  return map;
}

function mapStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function mapCategories(value: unknown): Record<string, VehicleEventType[]> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { ...DEFAULT_EVENT_CATEGORIES };
  }
  const categories: Record<string, VehicleEventType[]> = {};
  for (const [category, types] of Object.entries(value as Record<string, unknown>)) {
    categories[category] = mapStringList(types);
  }
  return categories;
}

function eventTypesFrom(categories: Record<string, VehicleEventType[]>): VehicleEventType[] {
  return Object.values(categories).flat();
}

export async function getNotificationPreferences(): Promise<NotificationPrefs> {
  const response = await apiRequest<RemotePrefsResponse>("/api/notification-preferences");
  const categories = mapCategories(response.categories);
  const eventTypes = mapStringList(response.event_types);
  return {
    toast: mapBooleanMap(response.data?.toast),
    sound: mapBooleanMap(response.data?.sound),
    eventTypes: eventTypes.length > 0 ? eventTypes : eventTypesFrom(categories),
    categories,
  };
}

export interface NotificationPrefsChanges {
  toast?: Partial<Record<VehicleEventType, boolean>>;
  sound?: Partial<Record<VehicleEventType, boolean>>;
}

/** Sends only the changed types; returns the full, updated maps upstream answers with. */
export async function updateNotificationPreferences(
  changes: NotificationPrefsChanges,
): Promise<Pick<NotificationPrefs, "toast" | "sound">> {
  const response = await apiRequest<RemotePrefsResponse>("/api/notification-preferences", {
    method: "PUT",
    body: JSON.stringify(changes),
  });
  return {
    toast: mapBooleanMap(response.data?.toast),
    sound: mapBooleanMap(response.data?.sound),
  };
}

/** Missing from the map — a type the user never touched, or one newer than their save — is on. */
export function isToastEnabled(
  toast: Record<VehicleEventType, boolean> | undefined,
  type: VehicleEventType,
): boolean {
  return toast?.[type] !== false;
}

/** Sound is opt-in: only an explicit `true` beeps. */
export function isSoundEnabled(
  sound: Record<VehicleEventType, boolean> | undefined,
  type: VehicleEventType,
): boolean {
  return sound?.[type] === true;
}

export type { NotificationCategory };
